import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, Layers, FolderKanban, Star, LogOut, Plus, Trash2, Pencil, X, Loader2, MailOpen } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const LANGS = ["pt", "ru", "en", "fr"];
const CATEGORIES = ["fachadas", "pintura", "altura", "reparacao", "outro"];

const inp =
  "w-full border border-white/15 bg-[#050505] px-3 py-2.5 text-sm text-white placeholder-neutral-600 outline-none transition-colors focus:border-[#FF5A00]";

const LocField = ({ label, value = {}, onChange, textarea }) => (
  <div>
    <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-neutral-500">{label}</p>
    <div className="grid gap-2 sm:grid-cols-2">
      {LANGS.map((l) =>
        textarea ? (
          <textarea
            key={l}
            rows={2}
            placeholder={`${label} — ${l.toUpperCase()}`}
            value={value[l] || ""}
            onChange={(e) => onChange({ ...value, [l]: e.target.value })}
            className={inp}
          />
        ) : (
          <input
            key={l}
            placeholder={`${label} — ${l.toUpperCase()}`}
            value={value[l] || ""}
            onChange={(e) => onChange({ ...value, [l]: e.target.value })}
            className={inp}
          />
        )
      )}
    </div>
  </div>
);

const BenefitsField = ({ value = {}, onChange }) => (
  <div>
    <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-neutral-500">
      Benefits (comma separated, per language)
    </p>
    <div className="grid gap-2 sm:grid-cols-2">
      {LANGS.map((l) => (
        <input
          key={l}
          placeholder={`Benefits — ${l.toUpperCase()}`}
          value={Array.isArray(value[l]) ? value[l].join(", ") : value[l] || ""}
          onChange={(e) => onChange({ ...value, [l]: e.target.value })}
          className={inp}
        />
      ))}
    </div>
  </div>
);

const EMPTY = {
  service: { icon: "HardHat", image: "", title: {}, desc: {}, benefits: {} },
  project: { category: "fachadas", city: "", image: "", before: "", after: "", title: {}, workType: {}, desc: {} },
  review: { name: "", rating: 5, avatar: "", text: {} },
};

const normalizeBenefits = (b) =>
  Object.fromEntries(
    LANGS.map((l) => [
      l,
      Array.isArray(b?.[l]) ? b[l] : (b?.[l] || "").split(",").map((s) => s.trim()).filter(Boolean),
    ])
  );

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null); // {type, id|null, form}

  const load = useCallback(async () => {
    try {
      const [l, s, p, r] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/services"),
        api.get("/projects"),
        api.get("/reviews"),
      ]);
      setLeads(l.data);
      setServices(s.data);
      setProjects(p.data);
      setReviews(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        load();
      })
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate, load]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("proalp_token");
    navigate("/admin/login");
  };

  const startEdit = (type, item) => {
    const base = EMPTY[type];
    const form = item ? { ...base, ...item } : { ...base };
    setEditing({ type, id: item?.id || null, form });
  };

  const save = async () => {
    const { type, id, form } = editing;
    const payload = { ...form };
    if (type === "service") payload.benefits = normalizeBenefits(form.benefits);
    if (type === "review") payload.rating = Number(form.rating) || 5;
    const coll = type === "service" ? "services" : type === "project" ? "projects" : "reviews";
    try {
      if (id) await api.put(`/admin/${coll}/${id}`, payload);
      else await api.post(`/admin/${coll}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (coll, id) => {
    try {
      await api.delete(`/admin/${coll}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (checking)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]">
        <Loader2 className="animate-spin text-[#FF5A00]" />
      </div>
    );
  if (!user) return null;

  const tabs = [
    { id: "leads", label: "Leads", icon: Inbox, count: leads.filter((l) => !l.read).length },
    { id: "services", label: "Services", icon: Layers },
    { id: "projects", label: "Projects", icon: FolderKanban },
    { id: "reviews", label: "Reviews", icon: Star },
  ];

  const entityLists = { service: services, project: projects, review: reviews };
  const collOf = { service: "services", project: "projects", review: "reviews" };

  return (
    <div className="flex min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <aside className="flex w-16 flex-col border-r border-white/10 bg-[#070707] md:w-56">
        <div className="hidden px-5 py-5 md:block">
          <img src="/logo.png" alt="PROALP22" className="h-11 w-auto" />
        </div>
        <nav className="mt-4 flex flex-1 flex-col gap-1 px-2 md:px-4">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              data-testid={`admin-tab-${tb.id}`}
              onClick={() => {
                setTab(tb.id);
                setEditing(null);
              }}
              className={`flex items-center gap-3 px-3 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
                tab === tb.id ? "bg-[#FF5A00] text-white" : "text-neutral-400 hover:bg-white/5 hover:text-white"
              }`}
            >
              <tb.icon size={16} />
              <span className="hidden md:inline">{tb.label}</span>
              {tb.count > 0 && (
                <span className="ml-auto hidden h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[10px] text-black md:flex">
                  {tb.count}
                </span>
              )}
            </button>
          ))}
        </nav>
        <button
          data-testid="admin-logout"
          onClick={logout}
          className="m-4 flex items-center gap-3 px-3 py-3 text-xs font-bold uppercase tracking-widest text-neutral-500 transition-colors hover:text-red-400"
        >
          <LogOut size={16} />
          <span className="hidden md:inline">Logout</span>
        </button>
      </aside>

      <main className="flex-1 overflow-x-hidden p-5 md:p-10">
        {tab === "leads" && (
          <div data-testid="admin-leads">
            <h1 className="font-display text-4xl uppercase tracking-wide">Leads</h1>
            <div className="mt-8 overflow-x-auto border border-white/10">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-[#0A0A0A] text-[11px] uppercase tracking-widest text-neutral-500">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Service</th>
                    <th className="px-4 py-3">Message</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-neutral-500" data-testid="leads-empty">
                        No leads yet
                      </td>
                    </tr>
                  )}
                  {leads.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`lead-row-${l.id}`}
                      className={`border-t border-white/5 ${l.read ? "text-neutral-500" : "text-white"}`}
                    >
                      <td className="px-4 py-3 text-xs">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 font-semibold">{l.name}</td>
                      <td className="px-4 py-3">{l.phone}</td>
                      <td className="px-4 py-3">{l.email}</td>
                      <td className="px-4 py-3">{l.service}</td>
                      <td className="max-w-[220px] truncate px-4 py-3">{l.message}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {!l.read && (
                            <button
                              data-testid={`lead-read-${l.id}`}
                              onClick={async () => {
                                await api.patch(`/admin/leads/${l.id}/read`);
                                load();
                              }}
                              className="text-neutral-400 hover:text-[#FF5A00]"
                              title="Mark read"
                            >
                              <MailOpen size={16} />
                            </button>
                          )}
                          <button
                            data-testid={`lead-delete-${l.id}`}
                            onClick={async () => {
                              await api.delete(`/admin/leads/${l.id}`);
                              load();
                            }}
                            className="text-neutral-400 hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab !== "leads" && (
          <div>
            <div className="flex items-center justify-between">
              <h1 className="font-display text-4xl uppercase tracking-wide">{tab}</h1>
              <button
                data-testid={`admin-add-${tab}`}
                onClick={() => startEdit(tab.slice(0, -1), null)}
                className="flex items-center gap-2 bg-[#FF5A00] px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#E04F00]"
              >
                <Plus size={14} /> Add
              </button>
            </div>

            {editing && (
              <div data-testid="admin-form" className="mt-8 space-y-5 border border-white/10 bg-[#0A0A0A] p-6">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">
                    {editing.id ? "Edit" : "New"} {editing.type}
                  </p>
                  <button data-testid="admin-form-close" onClick={() => setEditing(null)}>
                    <X size={18} className="text-neutral-500 hover:text-white" />
                  </button>
                </div>
                {editing.type === "service" && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input placeholder="Image URL" value={editing.form.image} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, image: e.target.value } })} className={inp} data-testid="form-image" />
                      <input placeholder="Icon (lucide name)" value={editing.form.icon} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, icon: e.target.value } })} className={inp} />
                    </div>
                    <LocField label="Title" value={editing.form.title} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, title: v } })} />
                    <LocField label="Description" textarea value={editing.form.desc} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, desc: v } })} />
                    <BenefitsField value={editing.form.benefits} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, benefits: v } })} />
                  </>
                )}
                {editing.type === "project" && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <select value={editing.form.category} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, category: e.target.value } })} className={inp} data-testid="form-category">
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <input placeholder="City" value={editing.form.city} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, city: e.target.value } })} className={inp} data-testid="form-city" />
                    </div>
                    <input placeholder="Image URL" value={editing.form.image} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, image: e.target.value } })} className={inp} data-testid="form-image" />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input placeholder="Before photo URL (optional)" value={editing.form.before} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, before: e.target.value } })} className={inp} />
                      <input placeholder="After photo URL (optional)" value={editing.form.after} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, after: e.target.value } })} className={inp} />
                    </div>
                    <LocField label="Title" value={editing.form.title} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, title: v } })} />
                    <LocField label="Work type" value={editing.form.workType} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, workType: v } })} />
                    <LocField label="Description" textarea value={editing.form.desc} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, desc: v } })} />
                  </>
                )}
                {editing.type === "review" && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <input placeholder="Client name" value={editing.form.name} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })} className={inp} data-testid="form-name" />
                      <input type="number" min="1" max="5" placeholder="Rating 1-5" value={editing.form.rating} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, rating: e.target.value } })} className={inp} />
                      <input placeholder="Avatar URL (optional)" value={editing.form.avatar} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, avatar: e.target.value } })} className={inp} />
                    </div>
                    <LocField label="Review text" textarea value={editing.form.text} onChange={(v) => setEditing({ ...editing, form: { ...editing.form, text: v } })} />
                  </>
                )}
                <button data-testid="admin-form-save" onClick={save} className="bg-[#FF5A00] px-8 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#E04F00]">
                  Save
                </button>
              </div>
            )}

            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {entityLists[tab.slice(0, -1)].map((item) => (
                <div key={item.id} data-testid={`admin-item-${item.id}`} className="border border-white/10 bg-[#0A0A0A] p-5">
                  {item.image && <img src={item.image} alt="" className="mb-4 h-32 w-full object-cover" />}
                  {item.avatar && <img src={item.avatar} alt="" className="mb-4 h-12 w-12 rounded-full object-cover" />}
                  <p className="font-display text-xl uppercase tracking-wide">{item.title?.pt || item.name}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-neutral-500">{item.desc?.pt || item.text?.pt}</p>
                  <div className="mt-4 flex gap-2">
                    <button
                      data-testid={`admin-edit-${item.id}`}
                      onClick={() => startEdit(tab.slice(0, -1), item)}
                      className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:border-[#FF5A00] hover:text-[#FF5A00]"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      data-testid={`admin-delete-${item.id}`}
                      onClick={() => remove(collOf[tab.slice(0, -1)], item.id)}
                      className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:border-red-400 hover:text-red-400"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
